/**
 * Pure key-combo helpers for the shortcut system — no React, no DOM listeners,
 * so matching is unit-testable in isolation (the single `keydown` listener lives
 * in `src/shared/components/ShortcutHost.tsx`).
 *
 * A combo is a `+`-joined string of modifier tokens then one primary key, e.g.
 * `mod+b`, `shift+/`, `mod+shift+k`. The `mod` token resolves to ⌘ (metaKey) on
 * macOS and Ctrl (ctrlKey) elsewhere, so a single binding works cross-platform.
 *
 * The primary key is compared against `KeyboardEvent.key`, so it is that value
 * lowercased (`arrowleft`, `,`, `b`) — with named aliases in `KEY_ALIASES`
 * (`src/shared/config/keyAliases.ts`) for keys whose `key` value can't appear
 * literally in a `+`-joined string.
 */

import { KEY_ALIASES } from '@/shared/config/keyAliases';

/** A key-combo descriptor, e.g. `'mod+b'`. */
export type KeyCombo = string;

interface ParsedCombo {
  /** Primary key, lowercased. */
  key: string;
  mod: boolean;
  ctrl: boolean;
  meta: boolean;
  shift: boolean;
  alt: boolean;
}

/** macOS uses ⌘ (metaKey) as the primary modifier; every other platform uses Ctrl. */
function isMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent);
}

/** Parse a combo string into its modifiers and (lowercased) primary key. */
function parseCombo(combo: KeyCombo): ParsedCombo {
  const tokens = combo
    .split('+')
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const parsed: ParsedCombo = {
    key: '',
    mod: false,
    ctrl: false,
    meta: false,
    shift: false,
    alt: false,
  };
  for (const token of tokens) {
    switch (token) {
      case 'mod':
        parsed.mod = true;
        break;
      case 'ctrl':
      case 'control':
        parsed.ctrl = true;
        break;
      case 'meta':
      case 'cmd':
      case 'command':
        parsed.meta = true;
        break;
      case 'shift':
        parsed.shift = true;
        break;
      case 'alt':
      case 'option':
        parsed.alt = true;
        break;
      default:
        // Last non-modifier token wins as the primary key.
        parsed.key = KEY_ALIASES[token] ?? token;
    }
  }
  return parsed;
}

/**
 * Test a `KeyboardEvent` against a combo. The primary key is compared
 * case-insensitively (so Shift/CapsLock producing `'B'` still matches `b`), and
 * every modifier must match exactly — an event carrying an undeclared modifier
 * does not match.
 */
export function matchesCombo(event: KeyboardEvent, combo: KeyCombo): boolean {
  // `KeyboardEvent.key` is a string per spec, but real events dispatched by
  // autofill / IME / password managers can arrive without it — guard so the
  // global keydown listener never throws.
  if (!event.key) return false;
  const parsed = parseCombo(combo);
  const needMeta = parsed.meta || (parsed.mod && isMac());
  const needCtrl = parsed.ctrl || (parsed.mod && !isMac());
  return (
    event.key.toLowerCase() === parsed.key &&
    event.metaKey === needMeta &&
    event.ctrlKey === needCtrl &&
    event.shiftKey === parsed.shift &&
    event.altKey === parsed.alt
  );
}
